import React, { useContext, useState } from "react";
import "./BranchesDetails.css";
import { Context } from "../../../context/Context";
import { useAccess } from "../../../access/hooks/useAccess";
import WorkTime from "./workTime/WorkTime";
import BranchAppsUsers from "./branchAppsUsers/BranchAppsUsers";
import { DeleteModal } from "../../modals/Delete";
import { translations } from "../../../utils/lang";
import { Button, Input, InlineLink, StatusDropdown } from "../../ui";
import { Repeat, Trash2 } from "../../ui/icons";
import { tx } from "../../ludConnect/ludConnect.constants";

export const BranchesDetails = ({
  branches = [],
  onUpdate,
  branchesLimit = 1,
  restaurantId,
}) => {
  const { lang } = useContext(Context);
  const { can } = useAccess();
  const resource = { restaurantId };
  const canUpdateBranches = can("restaurant.branch.update", resource);

  const [list, setList] = useState(branches);
  const [editIndex, setEditIndex] = useState(null);
  const [newBranch, setNewBranch] = useState(null);
  const [expandedField, setExpandedField] = useState(null);
  const [editedBranch, setEditedBranch] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const toggleField = (index, field) => {
    const key = `${index}-${field}`;
    setExpandedField((prev) => (prev === key ? null : key));
  };

  const confirmDelete = (index) => {
    setDeleteTarget(index);
  };

  const handleConfirmedDelete = (index) => {
    if (!can("restaurant.branch.delete", resource)) return;
    if (editIndex !== null) return;
    const updated = [...list];
    updated.splice(index, 1);
    setList(updated);
    onUpdate && onUpdate(updated);
    setDeleteTarget(null);
  };

  const handleSave = () => {
    if (!can("restaurant.branch.update", resource)) return;
    if (editIndex !== null) {
      const updated = [...list];
      updated[editIndex] = { ...editedBranch };
      setList(updated);
      setEditIndex(null);
      setEditedBranch(null);
      onUpdate && onUpdate(updated);
    }
  };

  const handleCancel = () => {
    if (editIndex !== null) {
      setEditIndex(null);
      setEditedBranch(null);
    } else if (newBranch !== null) {
      setNewBranch(null);
    }
  };

  const handleCreate = () => {
    if (!can("restaurant.branch.create", resource)) return;
    if (newBranch?.name && newBranch?.phone && newBranch?.city) {
      const newList = [
        ...list,
        { ...newBranch, date: new Date().toISOString(), workTime: [] },
      ];
      setList(newList);
      setNewBranch(null);
      onUpdate && onUpdate(newList);
    }
  };

  const handleInputChange = (index, key, value, appsUsers = false) => {
    setList((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [key]: value,
      };
      if (appsUsers) {
        onUpdate && onUpdate(updated);
      }
      return updated;
    });
  };

  return (
    <div className="branches-container">
      <h3>
        {translations[lang].restaurants.restaurantsRow.BranchesDetails.title}
      </h3>

      <div className="restaurant-subtable-scroll">
        <table className="branches-table-container">
          <thead>
            <tr>
              {translations[
                lang
              ].restaurants.restaurantsRow.BranchesDetails.branchesDetailsHeaders.map(
                (item, i) => {
                  if (
                    translations[lang].restaurants.restaurantsRow.BranchesDetails
                      .branchesDetailsHeaders.length ===
                      i + 1 &&
                    !canUpdateBranches
                  )
                    return null;

                  return (
                    <th className="branches-table-header" key={i}>
                      {item}
                    </th>
                  );
                },
              )}
            </tr>
          </thead>

          <tbody>
            {Array.isArray(list) &&
              list.map((branch, i) => (
                <React.Fragment key={i}>
                  <tr>
                    <td className="branches-table-p">
                      {branch.date ? new Date(branch.date).toLocaleString() : "-"}
                    </td>

                    {can("restaurant.branch.details.open") ? (
                      <td
                        className="branches-table-p clickable"
                        onClick={() => toggleField(i, "name")}
                      >
                        <div className="branch-name-row clickable-p">
                          {branch.apps.map((u, appIndex) => {
                            if (u.status === "active")
                              return (
                                <div
                                  key={appIndex}
                                  className={`active-apps-to-branch ${u.app}`}
                                  title={u.app}
                                ></div>
                              );
                            return null;
                          })}
                          <p>{branch.name}</p>
                        </div>
                      </td>
                    ) : (
                      <td className="branches-table-p">
                        <p>{branch.name}</p>
                      </td>
                    )}

                    {can("restaurant.branch.phone.open") ? (
                      <td
                        className="branches-table-p clickable"
                        onClick={() => toggleField(i, "phone")}
                      >
                        <p className="clickable-p">{branch.phone}</p>
                      </td>
                    ) : (
                      <td className="branches-table-p">{branch.phone}</td>
                    )}

                    {can("restaurant.branch.workTime.open") ? (
                      <td
                        className="branches-table-p clickable"
                        onClick={() => toggleField(i, "worktime")}
                      >
                        <p className="clickable-p">{branch.city}</p>
                      </td>
                    ) : (
                      <td className="branches-table-p">{branch.city}</td>
                    )}

                    <td className="branches-table-p">
                      <InlineLink
                        href={branch.location}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {
                          translations[lang].restaurants.restaurantsRow.details
                            .linkHere
                        }
                      </InlineLink>
                    </td>

                    <td
                      className={`branches-table-p available ${
                        branch.status === "active" ? "" : "yellow"
                      }`}
                    >
                      <StatusDropdown
                        value={branch.status}
                        options={[
                          "new",
                          "active",
                          "closed",
                          "inactive",
                          "inProgress",
                          "maintenance",
                        ]}
                        editable={can("restaurant.branch.status.update", resource)}
                        getLabel={(value) =>
                          ({
                            new: tx("جديد", "New", lang === "ar"),
                            inProgress: tx(
                              "قيد المعالجة",
                              "In progress",
                              lang === "ar",
                            ),
                            active: tx("نشط", "Active", lang === "ar"),
                            inactive: tx("غير مفعل", "Inactive", lang === "ar"),
                            closed: tx("مغلق", "Closed", lang === "ar"),
                            maintenance: tx(
                              "صيانة",
                              "Maintenance",
                              lang === "ar",
                            ),
                          })[value] || value
                        }
                        onChange={(val) => {
                          const updated = [...list];
                          updated[i] = { ...list[i], status: val,statusChangedAt: new Date(), };
                          setList(updated);
                          onUpdate && onUpdate(updated);
                        }}
                      />
                    </td>

                    {canUpdateBranches && (
                      <td className="branches-table-btns">
                        <Button
                          type="button"
                          variant="primary"
                          appearance="soft"
                          icon={<Repeat size={16} />}
                          onClick={() => {
                            setEditIndex(i);
                            setEditedBranch({ ...list[i] });
                          }}
                        />

                        {can("restaurant.branch.delete", resource) && (
                          <Button
                            type="button"
                            variant="danger"
                            appearance=""
                            icon={<Trash2 size={18} />}
                            onClick={() => confirmDelete(i)}
                          />
                        )}
                      </td>
                    )}
                  </tr>

                  {expandedField === `${i}-name` && (
                    <tr>
                      <td colSpan="7">
                        <BranchAppsUsers
                          type="apps"
                          restaurantId={restaurantId}
                          data={branch.apps}
                          onUpdate={(updatedApps) =>
                            handleInputChange(i, "apps", updatedApps, true)
                          }
                        />
                      </td>
                    </tr>
                  )}

                  {expandedField === `${i}-phone` && (
                    <tr>
                      <td colSpan="7">
                        <BranchAppsUsers
                          restaurantId={restaurantId}
                          branchId={branch._id}
                          type="userInfo"
                          data={branch.userInfo}
                          onUpdate={(updatedInfo) =>
                            handleInputChange(i, "userInfo", updatedInfo, true)
                          }
                        />
                      </td>
                    </tr>
                  )}

                  {expandedField === `${i}-worktime` && (
                    <tr>
                      <td colSpan="7">
                        <WorkTime
                          restaurantId={restaurantId}
                          branches={list}
                          setBranches={setList}
                          branchIndex={i}
                          onSave={() => onUpdate(list)}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
          </tbody>
        </table>
      </div>

      {(editIndex !== null || newBranch) && (
        <div className="branch-form-grid-container">
          <div className="branch-form-grid">
            <div>
              <p>
                {
                  translations[lang].restaurants.restaurantsRow.BranchesDetails
                    .branchesDetailsHeaders[1]
                }
              </p>
              <Input
                placeholder="name"
                value={
                  editIndex !== null
                    ? editedBranch?.name || ""
                    : newBranch?.name || ""
                }
                onChange={(e) => {
                  editIndex !== null
                    ? setEditedBranch({ ...editedBranch, name: e.target.value })
                    : setNewBranch({ ...newBranch, name: e.target.value });
                }}
              />
            </div>

            <div>
              <p>
                {
                  translations[lang].restaurants.restaurantsRow.BranchesDetails
                    .branchesDetailsHeaders[2]
                }
              </p>
              <Input
                placeholder="0555"
                value={
                  editIndex !== null
                    ? editedBranch?.phone || ""
                    : newBranch?.phone || ""
                }
                onChange={(e) => {
                  editIndex !== null
                    ? setEditedBranch({
                        ...editedBranch,
                        phone: e.target.value,
                      })
                    : setNewBranch({ ...newBranch, phone: e.target.value });
                }}
              />
            </div>

            <div>
              <p>
                {
                  translations[lang].restaurants.restaurantsRow.BranchesDetails
                    .branchesDetailsHeaders[3]
                }
              </p>
              <Input
                placeholder="city"
                value={
                  editIndex !== null
                    ? editedBranch?.city || ""
                    : newBranch?.city || ""
                }
                onChange={(e) => {
                  editIndex !== null
                    ? setEditedBranch({ ...editedBranch, city: e.target.value })
                    : setNewBranch({ ...newBranch, city: e.target.value });
                }}
              />
            </div>

            <div>
              <p>
                {
                  translations[lang].restaurants.restaurantsRow.BranchesDetails
                    .branchesDetailsHeaders[4]
                }
              </p>
              <Input
                placeholder="https://"
                value={
                  editIndex !== null
                    ? editedBranch?.location || ""
                    : newBranch?.location || ""
                }
                onChange={(e) => {
                  editIndex !== null
                    ? setEditedBranch({
                        ...editedBranch,
                        location: e.target.value,
                      })
                    : setNewBranch({ ...newBranch, location: e.target.value });
                }}
              />
            </div>

            <div>
              <p>
                {
                  translations[lang].restaurants.restaurantsRow.BranchesDetails
                    .coordination
                }
              </p>
              <Input
                placeholder="H647+3C..."
                value={
                  editIndex !== null
                    ? editedBranch?.coordination || ""
                    : newBranch?.coordination || ""
                }
                onChange={(e) => {
                  editIndex !== null
                    ? setEditedBranch({
                        ...editedBranch,
                        coordination: e.target.value,
                      })
                    : setNewBranch({
                        ...newBranch,
                        coordination: e.target.value,
                      });
                }}
              />
            </div>

            <div>
              <p>
                {
                  translations[lang].restaurants.restaurantsRow.BranchesDetails
                    .branchesDetailsHeaders[5]
                }
              </p>
              <StatusDropdown
                value={
                  editIndex !== null ? editedBranch?.status : newBranch?.status
                }
                options={[
                  "new",
                  "active",
                  "closed",
                  "inactive",
                  "inProgress",
                  "maintenance",
                ]}
                getLabel={(value) =>
                  ({
                    new: tx("جديد", "New", lang === "ar"),
                    inProgress: tx(
                      "قيد المعالجة",
                      "In progress",
                      lang === "ar",
                    ),
                    active: tx("نشط", "Active", lang === "ar"),
                    inactive: tx("غير مفعل", "Inactive", lang === "ar"),
                    closed: tx("مغلق", "Closed", lang === "ar"),
                    maintenance: tx("صيانة", "Maintenance", lang === "ar"),
                  })[value] || value
                }
                onChange={(val) => {
                  editIndex !== null
                    ? setEditedBranch({ ...editedBranch, status: val, statusChangedAt: new Date(), })
                    : setNewBranch({ ...newBranch, status: val, statusChangedAt: new Date(), });
                }}
              />
            </div>
          </div>

          <div className="branches-btn-group">
            {editIndex !== null ? (
              <>
                <Button
                  type="button"
                  variant="primary"
                  appearance="filled"
                  fullWidth
                  onClick={handleSave}
                >
                  {
                    translations[lang].restaurants.restaurantsRow
                      .BranchesDetails.save
                  }
                </Button>

                <Button
                  type="button"
                  variant="danger"
                  appearance="soft"
                  fullWidth
                  onClick={handleCancel}
                >
                  {
                    translations[lang].restaurants.restaurantsRow
                      .BranchesDetails.cancel
                  }
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  variant="primary"
                  appearance="filled"
                  fullWidth
                  onClick={handleCreate}
                >
                  {
                    translations[lang].restaurants.restaurantsRow
                      .BranchesDetails.add
                  }
                </Button>

                <Button
                  type="button"
                  variant="danger"
                  appearance="soft"
                  fullWidth
                  onClick={handleCancel}
                >
                  {
                    translations[lang].restaurants.restaurantsRow
                      .BranchesDetails.cancel
                  }
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {can("restaurant.branch.create", resource) &&
        !newBranch &&
        editIndex === null &&
        list.length < branchesLimit && (
          <Button
            type="button"
            variant="primary"
            appearance="soft"
            onClick={() => setNewBranch({})}
          >
            {
              translations[lang].restaurants.restaurantsRow.BranchesDetails
                .newBranch
            }
          </Button>
        )}

      {deleteTarget !== null && (
        <DeleteModal
          id={deleteTarget}
          handleDelete={handleConfirmedDelete}
          cancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};
